'use client';

import { useMemo, useState, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import {
  Package,
  Home,
  Store,
  ShoppingBag,
  Briefcase,
  Handshake,
  LifeBuoy,
  Newspaper,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Phone,
  RotateCcw,
} from 'lucide-react';
import FormConsentCheckboxes from '@/components/forms/FormConsentCheckboxes';
import { dispatchOtpOutcome } from '@/lib/analytics-events';
import { useToast } from '@/hooks/use-toast';
import {
  isContactFallbackFormComplete,
  isValidIndianMobile,
  isValidPersonName,
  normalizeIndianMobileInput,
  normalizePersonNameInput,
} from '@/components/forms/formValidation.utils';
import { gsap } from 'gsap';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { cn } from '@/components/ui/utils';
import type { LeadForm } from '@/types/lead-forms';
import DynamicLeadForm from './DynamicLeadForm';
import { normalizeLeadForms } from '@/lib/lead-forms';
import { stripHtml } from '@/lib/homepage-mappers';
import { matchCrmSubtype, type WCLCaseSubtype } from '@/lib/wcl-case';

type InquiryOption = {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  leadForm?: LeadForm;
  /** CRM `wcl_casesubtypeid` sent to CreateCaseWebsite as `subtype`. */
  subtypeId?: string;
};

/** Best-effort icon for a topic / form name. */
function iconForTopic(name: string): React.ElementType {
  const n = name.toLowerCase();
  if (n.includes('cement')) return Package;
  if (n.includes('home')) return Home;
  if (n.includes('dealer')) return Store;
  if (n.includes('retail')) return ShoppingBag;
  if (n.includes('career')) return Briefcase;
  if (n.includes('partner') || n.includes('associ')) return Handshake;
  if (n.includes('support') || n.includes('feedback')) return LifeBuoy;
  if (n.includes('media') || n.includes('corporate')) return Newspaper;
  return Package;
}

const FALLBACK_OPTIONS: InquiryOption[] = [
  {
    id: 'cement-requirement',
    icon: Package,
    title: 'Cement Requirement',
    description: 'Buy cement for your project',
  },
  {
    id: 'home-building-guidance',
    icon: Home,
    title: 'Home Building',
    description: 'Expert construction advice',
  },
  {
    id: 'dealer',
    icon: Store,
    title: 'Become a Dealer',
    description: 'Authorised dealer partnership',
  },
  {
    id: 'retailer',
    icon: ShoppingBag,
    title: 'Become a Retailer',
    description: 'Retail outlet partnership',
  },
  { id: 'careers', icon: Briefcase, title: 'Careers', description: 'Open roles at Wonder Cement' },
  {
    id: 'partnerships',
    icon: Handshake,
    title: 'Partnerships',
    description: 'Engineers, architects & contractors',
  },
  {
    id: 'support',
    icon: LifeBuoy,
    title: 'Support & Feedback',
    description: 'Get help or share feedback',
  },
  {
    id: 'media-corporate',
    icon: Newspaper,
    title: 'Media & Corporate',
    description: 'Press & sustainability queries',
  },
];

function continueHint(
  nameTrimmed: string,
  nameValid: boolean,
  phoneVerified: boolean,
  selected: InquiryOption | null,
): string {
  if (!nameTrimmed) return 'Enter your name to continue';
  if (!nameValid) return 'Enter a valid name (letters only)';
  if (!phoneVerified) return 'Verify your number to continue';
  if (!selected) return 'Pick a topic to continue';
  return '';
}

const inputCls =
  'contact-enquiry-field h-11 w-full rounded-xl border border-black/10 bg-[#f9f9f9] px-3.5 text-sm text-black focus-visible:border-[#E31E24] focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#E31E24]/10 transition-all outline-none shadow-none';
const textareaCls =
  'contact-enquiry-field w-full min-h-[6.5rem] resize-none rounded-xl border border-black/10 bg-[#f9f9f9] px-3.5 py-3 text-sm text-black focus-visible:border-[#E31E24] focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#E31E24]/10 transition-all outline-none shadow-none';

function FL({
  htmlFor,
  children,
  required,
}: {
  htmlFor: string;
  children: ReactNode;
  required?: boolean;
}) {
  return (
    <Label htmlFor={htmlFor} className="text-[12px] font-semibold text-[#333]">
      {children}
      {required && <span className="ml-0.5 text-[#E31E24]">*</span>}
    </Label>
  );
}

interface ContactInquiryPanelProps {
  leadForms?: LeadForm[];
}

function TopicBadgeIcon({ icon: Icon }: { icon: React.ElementType }) {
  return <Icon size={12} strokeWidth={2} className="text-[#E31E24]" />;
}

function PanelHeader({
  step,
  name,
  selectedTitle,
  onBack,
}: {
  step: 'select' | 'form';
  name: string;
  selectedTitle: string | undefined;
  onBack: () => void;
}) {
  if (step === 'select') {
    return (
      <>
        <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.24em] text-[#E31E24]">
          Get in touch
        </p>
        <h2 className="font-figtree text-[1.65rem] font-semibold leading-tight text-[#1C1917] sm:text-[1.85rem]">
          Let&rsquo;s start with the basics<span className="text-[#E31E24]">.</span>
        </h2>
        <p className="mt-1.5 text-[13px] text-black/35">
          Your name, number, and what you need that&rsquo;s all.
        </p>
      </>
    );
  }
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.24em] text-[#E31E24]">
          Step 2 of 2
        </p>
        <h2 className="font-figtree text-[1.65rem] font-semibold leading-tight text-[#1C1917] sm:text-[1.85rem]">
          {name.split(' ')[0]}, tell us more<span className="text-[#E31E24]">.</span>
        </h2>
        <p className="mt-1.5 text-[13px] text-black/35">
          Just the details about your{' '}
          <span className="font-semibold text-black/50">{selectedTitle}</span> inquiry.
        </p>
      </div>
      <button
        type="button"
        onClick={onBack}
        className="mt-1 flex shrink-0 items-center gap-1.5 rounded-full border border-black/10 bg-[#fafafa] px-3 py-1.5 text-[11px] font-semibold text-black/50 transition-all hover:border-black/20 hover:text-black/70"
      >
        <ArrowLeft size={11} strokeWidth={2.5} />
        Back
      </button>
    </div>
  );
}

export default function ContactInquiryPanel({ leadForms }: ContactInquiryPanelProps = {}) {
  const t = useTranslations('contact');

  // Topic list is driven by Strapi lead-forms (`formName` / `formDescription`).
  const normalizedForms = useMemo(
    () => (leadForms && leadForms.length > 0 ? normalizeLeadForms(leadForms) : []),
    [leadForms],
  );
  const defaultLeadForm = normalizedForms[0];

  // CRM subtypes supply the GUID for CreateCaseWebsite; matched onto each CMS form.
  const [crmTopics, setCrmTopics] = useState<WCLCaseSubtype[] | null>(null);
  useEffect(() => {
    let active = true;
    fetch('/api/wcl/case-subtypes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (active && Array.isArray(data)) setCrmTopics(data as WCLCaseSubtype[]);
      })
      .catch(() => {
        if (active) setCrmTopics([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const options: InquiryOption[] = useMemo(() => {
    if (normalizedForms.length > 0) {
      return normalizedForms.map((form) => {
        const crm = matchCrmSubtype(form.formName, crmTopics);
        return {
          id: form.formId || String(form.id),
          subtypeId: crm?.id,
          icon: iconForTopic(form.formName),
          title: form.formName,
          description: stripHtml(form.formDescription),
          leadForm: form,
        };
      });
    }
    // Static fallback only when lead-forms API returned nothing.
    return FALLBACK_OPTIONS.map((o) => ({ ...o, leadForm: defaultLeadForm }));
  }, [crmTopics, normalizedForms, defaultLeadForm]);

  const [step, setStep] = useState<'select' | 'form'>('select');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [selected, setSelected] = useState<InquiryOption | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const stepRef = useRef<HTMLDivElement>(null);

  // Reset the whole panel to a clean first step (used after a successful submit).
  function resetForm() {
    setName('');
    setPhone('');
    setPhoneVerified(false);
    setMarketingConsent(false);
    setTermsAccepted(false);
    setSelected(null);
    setSubmitted(false);
  }

  function animateIn() {
    if (!stepRef.current) return;
    gsap.fromTo(
      stepRef.current,
      { autoAlpha: 0, y: 20 },
      { autoAlpha: 1, y: 0, duration: 0.4, ease: 'power3.out' },
    );
  }

  function goToForm() {
    if (!canContinue) return;
    gsap.to(stepRef.current, {
      autoAlpha: 0,
      y: -16,
      duration: 0.25,
      ease: 'power2.in',
      onComplete: () => setStep('form'),
    });
  }

  function goBack() {
    gsap.to(stepRef.current, {
      autoAlpha: 0,
      y: 16,
      duration: 0.25,
      ease: 'power2.in',
      onComplete: () => {
        // After a successful submit, "Back" starts a fresh enquiry (clears
        // name/phone/verification/topic). Otherwise it just returns to the
        // topic picker with the entered details intact (e.g. to change topic).
        if (submitted) resetForm();
        setStep('select');
      },
    });
  }

  // Reset OTP verification if phone number changes
  useEffect(() => {
    setPhoneVerified(false);
  }, [phone]);

  useEffect(() => {
    animateIn();
  }, [step]);

  const nameValid = isValidPersonName(name);
  const canContinue = nameValid && phoneVerified && selected !== null;

  return (
    <section
      className="overflow-hidden rounded-2xl bg-white font-figtree shadow-[0_2px_24px_-4px_rgba(0,0,0,0.1),0_1px_4px_-1px_rgba(0,0,0,0.06)]"
      aria-label="Contact form"
    >
      {/* ── Header ── */}
      <div className="border-b border-black/[0.06] px-6 pb-5 pt-7 sm:px-8">
        <PanelHeader step={step} name={name} selectedTitle={selected?.title} onBack={goBack} />
      </div>

      {/* ── Step content ── */}
      <div ref={stepRef}>
        {step === 'select' ? (
          <div className="px-6 py-6 sm:px-8">
            {/* Name + Phone — same row */}
            <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-2 md:items-start">
              <div className="space-y-1.5">
                <FL htmlFor="cp-name" required>
                  Your Name
                </FL>
                <Input
                  type="text"
                  id="cp-name"
                  value={name}
                  onChange={(e) => setName(normalizePersonNameInput(e.target.value, 120))}
                  className={inputCls}
                  placeholder="Full name"
                  autoComplete="name"
                  maxLength={120}
                  aria-invalid={Boolean(name.trim()) && !nameValid}
                />
                {name.trim() && !nameValid ? (
                  <p className="text-[11px] text-[#E31E24]">
                    Please enter a valid name (letters only)
                  </p>
                ) : null}
              </div>
              <PhoneOtpField
                phone={phone}
                onPhoneChange={setPhone}
                verified={phoneVerified}
                onVerified={() => setPhoneVerified(true)}
              />
            </div>

            {/* Topic — animated custom dropdown */}
            <TopicDropdown options={options} selected={selected} onSelect={setSelected} />

            {/* Continue */}
            <div className="mt-6 flex items-center justify-between gap-4">
              <p className="text-[11px] text-black/30">
                {continueHint(name.trim(), nameValid, phoneVerified, selected)}
              </p>
              <button
                type="button"
                onClick={goToForm}
                disabled={!canContinue}
                className={cn(
                  'inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-bold transition-all duration-200',
                  canContinue
                    ? 'bg-[#E31E24] text-white shadow-[0_4px_16px_rgba(227,30,36,0.3)] hover:bg-[#c8181e] hover:shadow-[0_6px_24px_rgba(227,30,36,0.4)]'
                    : 'cursor-not-allowed bg-black/8 text-black/25',
                )}
              >
                Continue
                <ArrowRight size={14} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 py-6 sm:px-8">
            {/* Selected topic badge */}
            <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#E31E24]/8 px-3.5 py-1.5">
              <TopicBadgeIcon icon={selected!.icon} />
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#E31E24]">
                {selected!.title}
              </p>
            </div>

            {selected?.leadForm ? (
              <DynamicLeadForm
                key={selected.id}
                leadForm={selected.leadForm}
                prefillName={name}
                prefillPhone={phone}
                subtype={selected.subtypeId ?? selected.leadForm.formName ?? selected.title}
                phoneVerified={phoneVerified}
                marketingConsent={marketingConsent}
                onMarketingConsentChange={setMarketingConsent}
                privacyConsent={termsAccepted}
                onPrivacyConsentChange={setTermsAccepted}
                onSubmitted={() => setSubmitted(true)}
              />
            ) : (
              <FallbackForm
                key={selected!.id}
                option={selected!}
                name={name}
                phone={phone}
                t={t}
                marketingConsent={marketingConsent}
                onMarketingConsentChange={setMarketingConsent}
                privacyConsent={termsAccepted}
                onPrivacyConsentChange={setTermsAccepted}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
}

const OTP_LENGTH = 6;
const RESEND_SECONDS = 30;

/** Inline phone field with OTP verification that slides open below. */
function PhoneOtpField({
  phone,
  onPhoneChange,
  verified,
  onVerified,
}: {
  phone: string;
  onPhoneChange: (v: string) => void;
  verified: boolean;
  onVerified: () => void;
}) {
  const { toast } = useToast();
  const [otpSent, setOtpSent] = useState(false);
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [status, setStatus] = useState<'idle' | 'sending' | 'verifying' | 'error'>('idle');
  const [countdown, setCountdown] = useState(0);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const otpRowRef = useRef<HTMLDivElement>(null);

  // Countdown
  useEffect(() => {
    if (countdown <= 0) return;
    const id = window.setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [countdown]);

  // Slide OTP row in when otpSent flips true
  useEffect(() => {
    if (!otpSent || !otpRowRef.current) return;
    const row = otpRowRef.current;
    const boxes = row.querySelectorAll('[data-otp-box]');
    gsap.fromTo(
      row,
      { autoAlpha: 0, height: 0 },
      { autoAlpha: 1, height: 'auto', duration: 0.32, ease: 'power3.out' },
    );
    gsap.fromTo(
      boxes,
      { autoAlpha: 0, y: 10, scale: 0.8 },
      {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        duration: 0.35,
        ease: 'back.out(1.8)',
        stagger: 0.045,
        delay: 0.1,
      },
    );
    setTimeout(() => inputsRef.current[0]?.focus(), 160);
  }, [otpSent]);

  const normalizedPhone = normalizeIndianMobileInput(phone);
  const canSend = isValidIndianMobile(normalizedPhone);

  async function sendOtp() {
    if (!canSend || status === 'sending') return;
    setStatus('sending');
    try {
      const res = await fetch('/api/wcl/otp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: normalizedPhone }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to send OTP');
      }
      dispatchOtpOutcome('otp_sent', 'contact_phone_verification', 'sms', otpSent);
      setOtpSent(true);
      setCountdown(RESEND_SECONDS);
      setDigits(Array(OTP_LENGTH).fill(''));
      setStatus('idle');
    } catch (err) {
      setStatus('idle');
      toast({
        title: 'Couldn’t send OTP',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  }

  function handleChange(val: string, idx: number) {
    const char = val.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[idx] = char;
    setDigits(next);
    if (status === 'error') setStatus('idle');
    if (char && idx < OTP_LENGTH - 1) inputsRef.current[idx + 1]?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>, idx: number) {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) inputsRef.current[idx - 1]?.focus();
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = [...digits];
    pasted.split('').forEach((c, i) => {
      next[i] = c;
    });
    setDigits(next);
    inputsRef.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  }

  function shakeBoxes() {
    if (!otpRowRef.current) return;
    const boxes = otpRowRef.current.querySelectorAll('[data-otp-box]');
    gsap.fromTo(
      boxes,
      { x: 0 },
      {
        x: 5,
        duration: 0.07,
        ease: 'none',
        yoyo: true,
        repeat: 5,
        onComplete: () => {
          gsap.set(boxes, { x: 0 });
        },
      },
    );
  }

  function onVerifiedSuccess() {
    if (otpRowRef.current) {
      const boxes = otpRowRef.current.querySelectorAll('[data-otp-box]');
      gsap.to(boxes, {
        scale: 1.1,
        duration: 0.13,
        ease: 'power2.out',
        yoyo: true,
        repeat: 1,
        onComplete: () => {
          gsap.to(otpRowRef.current, {
            autoAlpha: 0,
            height: 0,
            duration: 0.28,
            ease: 'power2.in',
            onComplete: () => onVerified(),
          });
        },
      });
    } else {
      onVerified();
    }
  }

  function onVerifyFailed() {
    setStatus('error');
    shakeBoxes();
    setDigits(Array(OTP_LENGTH).fill(''));
    setTimeout(() => inputsRef.current[0]?.focus(), 50);
  }

  async function verify() {
    const otp = digits.join('');
    if (otp.length < OTP_LENGTH || status === 'verifying') return;
    setStatus('verifying');
    try {
      const res = await fetch('/api/wcl/otp/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: normalizedPhone, otp }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        dispatchOtpOutcome('otp_verified', 'contact_phone_verification');
        onVerifiedSuccess();
      } else {
        onVerifyFailed();
      }
    } catch {
      onVerifyFailed();
    }
  }

  function resendSms() {
    setDigits(Array(OTP_LENGTH).fill(''));
    setStatus('idle');
    sendOtp();
  }

  async function callMe() {
    if (!canSend || status === 'sending') return;
    setStatus('sending');
    try {
      const res = await fetch('/api/wcl/otp/voice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: normalizedPhone }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to place call');
      }
      dispatchOtpOutcome('otp_sent', 'contact_phone_verification', 'voice', otpSent);
      setOtpSent(true);
      setCountdown(RESEND_SECONDS);
      setDigits(Array(OTP_LENGTH).fill(''));
      setStatus('idle');
    } catch (err) {
      setStatus('idle');
      toast({
        title: 'Couldn’t place the call',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  }

  const otp = digits.join('');

  let sendOtpBtnCls: string;
  if (canSend && !otpSent) {
    sendOtpBtnCls = 'bg-[#E31E24] text-white hover:bg-[#c8181e]';
  } else if (otpSent) {
    sendOtpBtnCls = 'bg-[#E31E24]/10 text-[#E31E24]';
  } else {
    sendOtpBtnCls = 'cursor-not-allowed bg-black/6 text-black/25';
  }

  let sendOtpBtnLabel: React.ReactNode;
  if (status === 'sending') {
    sendOtpBtnLabel = (
      <span className="flex items-center gap-1.5">
        <span className="h-3 w-3 animate-spin rounded-full border-2 border-[#ffffff]/30 border-t-[#ffffff]" />
        Sending
      </span>
    );
  } else if (otpSent) {
    sendOtpBtnLabel = 'Sent ✓';
  } else {
    sendOtpBtnLabel = 'Send OTP';
  }

  return (
    <div className="space-y-1.5">
      <FL htmlFor="cp-phone" required>
        Phone Number
      </FL>

      {/* Phone input row */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative min-w-0 flex-1">
          <Input
            type="tel"
            id="cp-phone"
            inputMode="numeric"
            autoComplete="tel"
            maxLength={10}
            value={phone}
            onChange={(e) => {
              onPhoneChange(normalizeIndianMobileInput(e.target.value));
              setOtpSent(false);
              setStatus('idle');
            }}
            className={cn(
              inputCls,
              verified && 'border-emerald-400 bg-emerald-50/60 pr-8 text-emerald-700',
            )}
            placeholder="10-digit mobile number"
            readOnly={verified}
          />
          {verified && (
            <CheckCircle2
              size={15}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500"
              strokeWidth={2}
            />
          )}
        </div>

        {!verified && (
          <button
            type="button"
            data-analytics-event="otp_send_click"
            data-analytics-form="contact_phone_verification"
            onClick={sendOtp}
            disabled={!canSend || status === 'sending' || otpSent}
            className={cn(
              'w-full shrink-0 rounded-xl px-3.5 py-2.5 text-[12px] font-bold transition-all duration-200 sm:w-auto',
              sendOtpBtnCls,
            )}
          >
            {sendOtpBtnLabel}
          </button>
        )}
      </div>

      {/* Verified badge */}
      {verified && (
        <p className="flex items-center gap-1.5 text-[11.5px] font-medium text-emerald-600">
          <CheckCircle2 size={12} strokeWidth={2.5} />
          Number verified
        </p>
      )}

      {/* OTP entry — slides open below phone field */}
      {otpSent && !verified && (
        <div ref={otpRowRef} className="overflow-hidden">
          <div className="space-y-2.5 pt-2">
            {/* OTP boxes + inline Verify */}
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="grid min-w-0 flex-1 grid-cols-6 gap-1.5 sm:gap-2">
                {digits.map((d, i) => {
                  let otpBoxCls: string;
                  if (status === 'error') {
                    otpBoxCls = 'border-red-400 bg-red-50 text-red-600';
                  } else if (d) {
                    otpBoxCls = 'border-[#E31E24] bg-[#fff5f5] text-[#E31E24]';
                  } else {
                    otpBoxCls =
                      'border-black/15 bg-[#f9f9f9] focus:border-[#E31E24] focus:bg-white focus:shadow-[0_0_0_2px_rgba(227,30,36,0.08)]';
                  }
                  return (
                    <input
                      key={i}
                      data-otp-box
                      ref={(el) => {
                        inputsRef.current[i] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={d}
                      onChange={(e) => handleChange(e.target.value, i)}
                      onKeyDown={(e) => handleKeyDown(e, i)}
                      onPaste={i === 0 ? handlePaste : undefined}
                      className={cn(
                        'h-10 w-full min-w-0 rounded-lg border text-center text-base font-bold outline-none transition-all duration-150',
                        otpBoxCls,
                      )}
                    />
                  );
                })}
              </div>

              <button
                type="button"
                data-analytics-event="otp_verify_click"
                data-analytics-form="contact_phone_verification"
                onClick={verify}
                disabled={otp.length < OTP_LENGTH || status === 'verifying'}
                className={cn(
                  'inline-flex shrink-0 items-center gap-1 text-[13px] font-semibold transition-colors',
                  otp.length === OTP_LENGTH && status !== 'verifying'
                    ? 'text-[#E31E24] hover:text-[#c8181e]'
                    : 'cursor-not-allowed text-black/25',
                )}
              >
                {status === 'verifying' ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#E31E24]/30 border-t-[#E31E24]" />
                    Verifying
                  </span>
                ) : (
                  <>
                    Verify
                    <ArrowRight size={14} strokeWidth={2.5} />
                  </>
                )}
              </button>
            </div>

            {/* Status + countdown */}
            <div className="flex items-center justify-between gap-3">
              <p
                className={cn(
                  'text-[11px]',
                  status === 'error' ? 'font-medium text-red-500' : 'text-black/35',
                )}
              >
                {status === 'error' ? 'Incorrect code.' : `Code sent to ${phone}`}
              </p>
              {countdown > 0 && (
                <p className="shrink-0 text-[11px] tabular-nums text-black/35">
                  Resend in {countdown}s
                </p>
              )}
            </div>

            {/* Resend SMS / Call me — CTA pills, shown after countdown */}
            {countdown === 0 && (
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  data-analytics-event="otp_resend_click"
                  data-analytics-form="contact_phone_verification"
                  onClick={resendSms}
                  disabled={status === 'sending'}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[#E31E24]/30 bg-white px-3 py-1.5 text-[11.5px] font-semibold text-[#E31E24] transition-all hover:border-[#E31E24] hover:bg-[#E31E24]/5 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <RotateCcw size={12} strokeWidth={2.5} />
                  Resend OTP
                </button>
                <button
                  type="button"
                  data-analytics-event="otp_voice_click"
                  data-analytics-form="contact_phone_verification"
                  onClick={callMe}
                  disabled={status === 'sending'}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[#E31E24]/30 bg-white px-3 py-1.5 text-[11.5px] font-semibold text-[#E31E24] transition-all hover:border-[#E31E24] hover:bg-[#E31E24]/5 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Phone size={12} strokeWidth={2.5} />
                  Call me
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TopicDropdown({
  options,
  selected,
  onSelect,
}: {
  options: InquiryOption[];
  selected: InquiryOption | null;
  onSelect: (opt: InquiryOption) => void;
}) {
  const [open, setOpen] = useState(false);
  const [topicSearch, setTopicSearch] = useState('');
  const [rect, setRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Measure trigger position for fixed panel
  function openDropdown() {
    if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
    setTopicSearch('');
    setOpen(true);
  }

  const filteredOptions = useMemo(() => {
    const term = topicSearch.trim().toLowerCase();
    if (!term) return options;
    return options.filter(
      (opt) =>
        opt.title.toLowerCase().includes(term) || opt.description.toLowerCase().includes(term),
    );
  }, [options, topicSearch]);

  // Close on outside click or Escape
  useEffect(() => {
    if (!open) return;
    function onMouse(e: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      )
        setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    function reposition() {
      if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
    }
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  // Animate panel + stagger items
  useEffect(() => {
    if (!open || !panelRef.current) return;
    const panel = panelRef.current;
    const items = panel.querySelectorAll('[data-drop-item]');

    gsap.fromTo(
      panel,
      { autoAlpha: 0, y: -10, scaleY: 0.92, transformOrigin: 'top center' },
      { autoAlpha: 1, y: 0, scaleY: 1, duration: 0.28, ease: 'power3.out' },
    );
    gsap.fromTo(
      items,
      { autoAlpha: 0, y: -6 },
      { autoAlpha: 1, y: 0, duration: 0.22, ease: 'power2.out', stagger: 0.03, delay: 0.06 },
    );
  }, [open, filteredOptions]);

  const SelectedIcon = selected?.icon ?? Package;

  const panelStyle = (() => {
    if (!rect || typeof window === 'undefined') return null;

    const margin = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const width = Math.min(rect.width, viewportWidth - margin * 2);
    const left = Math.min(Math.max(rect.left, margin), viewportWidth - width - margin);
    const spaceBelow = viewportHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const openUpward = spaceBelow < 220 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(360, openUpward ? spaceAbove - 6 : spaceBelow - 6);

    return {
      position: 'fixed' as const,
      left,
      width,
      maxHeight: Math.max(160, maxHeight),
      zIndex: 9999,
      ...(openUpward ? { bottom: viewportHeight - rect.top + 6 } : { top: rect.bottom + 6 }),
    };
  })();

  let triggerCls: string;
  if (open) {
    triggerCls = 'border-[#E31E24] bg-white shadow-[0_0_0_3px_rgba(227,30,36,0.08)]';
  } else if (selected) {
    triggerCls = 'border-[#E31E24]/30 bg-[#fff8f8] hover:border-[#E31E24]/50';
  } else {
    triggerCls = 'border-black/10 bg-[#fafafa] hover:border-black/20 hover:bg-white';
  }

  return (
    <>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? setOpen(false) : openDropdown())}
        className={cn(
          'flex w-full items-center gap-3.5 rounded-xl border px-4 py-3.5 text-left transition-all duration-200',
          triggerCls,
        )}
      >
        <span
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-all duration-200',
            selected
              ? 'bg-[#E31E24] text-white shadow-[0_2px_8px_rgba(227,30,36,0.28)]'
              : 'bg-black/[0.06] text-[#999]',
          )}
        >
          <SelectedIcon size={15} strokeWidth={1.6} />
        </span>

        <span className="flex-1 min-w-0">
          {selected ? (
            <>
              <span className="block text-[13.5px] font-semibold text-[#1C1917]">
                {selected.title}
              </span>
              <span className="block truncate text-[11.5px] text-black/40 mt-0.5">
                {selected.description}
              </span>
            </>
          ) : (
            <span className="text-[13.5px] text-[#4c4c4c]">
              What would you like to connect about?
            </span>
          )}
        </span>

        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          className={cn(
            'shrink-0 text-black/25 transition-transform duration-300',
            open && 'rotate-180',
          )}
        >
          <path
            d="M3.5 5.5l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Portal panel — renders in document.body, escapes overflow clipping */}
      {open &&
        panelStyle &&
        createPortal(
          <div
            ref={panelRef}
            style={panelStyle}
            className="flex flex-col overflow-hidden rounded-2xl border border-black/[0.08] bg-white shadow-[0_16px_48px_rgba(0,0,0,0.14)]"
          >
            <div className="shrink-0 border-b border-black/[0.06] bg-white p-2">
              <Input
                type="text"
                value={topicSearch}
                onChange={(e) => setTopicSearch(e.target.value)}
                placeholder="Search topics…"
                aria-label="Search topics"
                className="h-9 rounded-lg border border-black/10 bg-[#fafafa] px-3 text-sm text-black shadow-none placeholder:!text-black/45 focus-visible:border-[#E31E24] focus-visible:ring-1 focus-visible:ring-[#E31E24]/20"
                autoFocus
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain">
              {filteredOptions.length === 0 ? (
                <p className="px-4 py-6 text-center text-[12.5px] text-black/40">No topics found</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2">
                  {filteredOptions.map((opt, i) => {
                    const active = selected?.id === opt.id;
                    const Icon = opt.icon;
                    const isRight = i % 2 === 1;
                    const isLastRow =
                      i >= filteredOptions.length - (filteredOptions.length % 2 === 0 ? 2 : 1);
                    return (
                      <button
                        key={opt.id}
                        data-drop-item
                        type="button"
                        onClick={() => {
                          onSelect(opt);
                          setOpen(false);
                        }}
                        className={cn(
                          'group flex items-start gap-3 px-4 py-3.5 text-left transition-all duration-150',
                          isRight && 'sm:border-l sm:border-black/[0.05]',
                          !isLastRow && 'border-b border-black/[0.05]',
                          active ? 'bg-[#fff5f5]' : 'hover:bg-[#fafafa]',
                        )}
                      >
                        <span
                          className={cn(
                            'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all duration-150',
                            active
                              ? 'bg-[#E31E24] text-white'
                              : 'bg-black/[0.05] text-[#777] group-hover:bg-[#E31E24]/10 group-hover:text-[#E31E24]',
                          )}
                        >
                          <Icon size={13} strokeWidth={1.7} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p
                            className={cn(
                              'text-[12.5px] font-semibold leading-snug',
                              active ? 'text-[#E31E24]' : 'text-[#1C1917]',
                            )}
                          >
                            {opt.title}
                          </p>
                          <p className="mt-0.5 text-[11px] leading-relaxed text-black/35">
                            {opt.description}
                          </p>
                        </div>
                        {active && (
                          <span
                            className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#E31E24]"
                            aria-hidden
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function FallbackForm({
  option,
  name,
  phone,
  t,
  marketingConsent,
  onMarketingConsentChange,
  privacyConsent,
  onPrivacyConsentChange,
}: {
  option: InquiryOption;
  name: string;
  phone: string;
  t: ReturnType<typeof useTranslations<'contact'>>;
  marketingConsent: boolean;
  onMarketingConsentChange: (checked: boolean) => void;
  privacyConsent: boolean;
  onPrivacyConsentChange: (checked: boolean) => void;
}) {
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(option.title);
  const [message, setMessage] = useState('');

  const canSubmit = isContactFallbackFormComplete({
    email,
    subject,
    message,
    privacyConsent,
    marketingConsent,
  });

  return (
    <form className="space-y-4">
      <input type="hidden" name="enquiryType" value={option.title} />
      <input type="hidden" name="name" value={name} />
      <input type="hidden" name="phone" value={phone} />

      <div className="space-y-1.5">
        <FL htmlFor="email" required>
          {t('form.email')}
        </FL>
        <Input
          type="email"
          id="email"
          name="email"
          className={inputCls}
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>

      <div className="space-y-1.5">
        <FL htmlFor="subject" required>
          {t('form.subject')}
        </FL>
        <Input
          type="text"
          id="subject"
          name="subject"
          className={inputCls}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
        />
      </div>

      <div className="space-y-1.5">
        <FL htmlFor="message" required>
          {t('form.message')}
        </FL>
        <Textarea
          id="message"
          name="message"
          rows={4}
          className={textareaCls}
          placeholder="Tell us about your requirement…"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required
        />
      </div>

      <FormConsentCheckboxes
        idPrefix="cp"
        showMarketing
        marketingConsent={marketingConsent}
        onMarketingConsentChange={onMarketingConsentChange}
        privacyConsent={privacyConsent}
        onPrivacyConsentChange={onPrivacyConsentChange}
      />

      <div className="flex items-center justify-between gap-4 pt-1">
        <p className="text-[11px] text-black/30">
          {!canSubmit
            ? 'Complete all required fields and accept checkboxes to submit'
            : '* Required fields'}
        </p>
        <SwapTextButton type="submit" text={t('form.submit')} disabled={!canSubmit} />
      </div>
    </form>
  );
}
